import { FormInstance, Col, Row, Space } from 'antd';
import { Rule } from 'antd/lib/form';
import { Dictionary, isEmpty } from 'lodash';
import { duration } from 'moment';
import React, { FC, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Company, PermissionSetType, UserType } from 'api/models';
import { FieldWithValue } from 'api/tasks';
import { Form } from 'components/atoms/form';
import { InputNumber } from 'components/atoms/input';
import { Radio } from 'components/atoms/radio';
import { CreatableSelect, Option, Select } from 'components/atoms/select';
import { DescriptiveOption } from 'components/atoms/select/custom-components';
import { InviteUserCreateableLabel } from 'components/atoms/select/invite-user-createable-label';
import { removeInsignificantTrailingZero } from 'components/organisms/resource-management/utils/dates';
import { Member } from 'data/members/api';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { useUsersQuery } from 'queries/members';
import { AccountPermissionSetType } from 'stores/permission-sets';
import { isValidEmail } from 'utils/is-valid-email';
import { TIMESHEET_ADMIN_ID } from '../drawer-modal/modal-body';
import { TeamMemberSectionField } from '../section-field/section-field';
import { TeamMemberDrawerSectionProps } from '../types';

const ErrorMessage = styled.div`
  color: var(--red-60);
`;

const StyledFormItem = styled(Form.Item)`
  margin-bottom: 0;

  ${Form.Item} {
    padding-bottom: 0 !important;
  }
`;

const InnerRow = styled(Row)`
  margin-top: var(--spacing-02);
`;

const TopMarginFormItem = styled(Form.Item)`
  margin-top: var(--spacing-05);
`;

export interface InviteMemberFormPayload {
  email: {
    label: string;
    value: string;
  }[];
  role?: {
    label?: string;
    value?: number;
  };
  permission?: {
    label?: string;
    value?: number;
  };
  timeSheetApprover?: Option;
  defaultCapacity: boolean;
  capacity: number;
  fields?: {
    fieldId?: number;
    fieldValue?: any;
  }[];
}

export const InviteModalFormV2: FC<{
  form: FormInstance<InviteMemberFormPayload>;
  member?: Member;
  company: Company;
  className?: string;
  approverOptions: Option[];
  sectionProps: TeamMemberDrawerSectionProps;
  permissionOptions: Option[];
  roleOptions: Option[];
  formInputRules?: Dictionary<Rule[]>;
  isVendorInvite: boolean;
}> = ({
  form,
  member,
  company,
  className,
  approverOptions,
  sectionProps,
  permissionOptions: permissionsList,
  roleOptions: roles,
  formInputRules = {},
  isVendorInvite,
}) => {
  const { data: users } = useUsersQuery();
  const { user: currentUser, permissions: currentUserPermissions } = useUser();

  const isSameUser = member?.user.userId === currentUser.userId;
  const isSuperUser =
    currentUserPermissions.permissionSet.permissionSetType ===
    PermissionSetType.SuperUser;

  const [capacity, setCapacity] = useState(
    member?.user.capacity ?? company.capacity,
  );
  const [defaultCapacity, setDefaultCapacity] = useState(
    member?.user.defaultCapacity ?? true,
  );
  const capacityInputPlaceholder = useTranslation(
    'settings.team_members.capacity.options.manual.input.placeholder',
  );
  const capacityInputError = useTranslation(
    'settings.weekly_capacity.input.error',
  );

  const existingUsersToCheck = useMemo(
    () =>
      isVendorInvite
        ? (users ?? []).filter((user) => user.userType !== UserType.External)
        : users ?? [],
    [isVendorInvite, users],
  );

  const emailIds = useMemo(
    () => existingUsersToCheck.map(({ emailId }) => emailId.toLowerCase()),
    [existingUsersToCheck],
  );

  const adminCaption = useTranslation(
    'settings.timesheets.controls.approvals.admin',
  );

  const adminOption = useMemo(
    () =>
      ({
        label: adminCaption,
        value: TIMESHEET_ADMIN_ID,
      } as Option),
    [adminCaption],
  );

  const formData: InviteMemberFormPayload | undefined = useMemo(() => {
    return member
      ? {
          email: [
            {
              label: member?.user.emailId || '',
              value: member?.user.emailId || '',
            },
          ],
          role: {
            label: member?.user.role?.roleName,
            value: member?.user.role?.roleId,
          },
          permission: {
            label: permissionsList.find(
              (x) => x.value === member?.permissionSet?.permissionSetType,
            )?.label,
            value: member?.permissionSet?.permissionSetType,
          },
          timeSheetApprover:
            approverOptions.find(
              (y) => y.value === member.user?.timeSheetApprover,
            ) ?? adminOption,
          defaultCapacity,
          capacity,
          fields: member?.user.fields,
        }
      : undefined;
  }, [
    adminOption,
    approverOptions,
    capacity,
    defaultCapacity,
    member,
    permissionsList,
  ]);

  if (member == null) {
    form.setFieldsValue({
      defaultCapacity,
      capacity,
    });
  }

  const onCustomFieldValueChange = (
    fieldValue,
    { fieldId }: FieldWithValue,
  ) => {
    const fields = form.getFieldValue('fields') ?? [];
    const field = fields.find((f) => f.fieldId === fieldId);

    if (field) {
      field.fieldValue = fieldValue;
    } else {
      fields.push({ fieldId, fieldValue });
    }

    form.setFieldsValue({ fields });
  };

  return (
    <Form
      form={form}
      initialValues={
        member
          ? formData
          : {
              timeSheetApprover: adminOption,
            }
      }
      layout="vertical"
      className={className}
    >
      <Form.Item
        label={useTranslation(
          `settings.team_members.${member ? 'edit' : 'invite'}.label.email`,
        )}
        name="email"
        rules={[
          {
            required: true,
            message: useTranslation(
              'settings.team_members.invite.validations.email',
            ),
          },
          ...(formInputRules.email ?? []),
        ]}
      >
        <CreatableSelect
          autoFocus
          isDisabled={Boolean(formData?.email[0])}
          showMenuOnOpen={false}
          isMulti
          type="user"
          isClearable
          options={[]}
          formatCreateLabel={(userInput) => (
            <InviteUserCreateableLabel value={userInput} />
          )}
          isValidNewOption={(input) =>
            isValidEmail(input) &&
            !emailIds.includes(input.trim().toLowerCase())
          }
          noOptionsMessage={({ inputValue }) => {
            const existingUser = existingUsersToCheck.find(
              (user) => user.emailId === inputValue.trim().toLowerCase(),
            );

            if (existingUser != null) {
              if (
                existingUser.company.companyId === currentUser.company.companyId
              ) {
                return (
                  <ErrorMessage>
                    <FormattedMessage id="request.invitation.errors.part_of_current_user_team" />
                  </ErrorMessage>
                );
              }

              return currentUser.userType === UserType.Native ? (
                <ErrorMessage>
                  <FormattedMessage
                    id="request.invitation.errors.part_of_another_team"
                    values={{
                      companyName: existingUser.company.companyName,
                      type: existingUser.company.companyType,
                    }}
                  />
                </ErrorMessage>
              ) : (
                <ErrorMessage>
                  <FormattedMessage id="request.invitation.errors.part_of_another_team.for_non_vendor" />
                </ErrorMessage>
              );
            }

            return <FormattedMessage id="validations.invite_email" />;
          }}
          placeholder={
            <FormattedMessage id="settings.team_members.invite.label.placeholder" />
          }
        />
      </Form.Item>
      <Row>
        <Col span="11">
          <Form.Item
            label={useTranslation('settings.team_members.invite.label.role')}
            name="role"
            rules={[
              {
                required: true,
                message: useTranslation(
                  'settings.team_members.invite.validations.role',
                ),
              },
            ]}
          >
            <CreatableSelect
              closeMenuOnSelect
              options={roles}
              isValidNewOption={(input) =>
                !sectionProps.hideRoleCreation && !isEmpty(input)
              }
              placeholder={
                sectionProps.hideRoleCreation === true ? (
                  <FormattedMessage id="settings.team_members.invite.placeholder.role_choose_only" />
                ) : (
                  <FormattedMessage id="settings.team_members.invite.placeholder.role" />
                )
              }
            />
          </Form.Item>
        </Col>
        <Col offset="1" span="12">
          <Form.Item
            label={
              <>
                {useTranslation(
                  'settings.team_members.invite.label.permission',
                )}
              </>
            }
            name="permission"
            rules={[
              {
                required: true,
                message: useTranslation(
                  'settings.team_members.invite.validations.permission',
                ),
              },
            ]}
          >
            <Select
              options={
                isSuperUser
                  ? permissionsList.filter(
                      (x) => x.value !== AccountPermissionSetType.Admin,
                    )
                  : permissionsList
              }
              isDisabled={
                (Boolean(formData?.permission) && isSameUser) ||
                (formData?.permission?.value ===
                  AccountPermissionSetType.Admin &&
                  isSuperUser)
              }
              components={{ Option: DescriptiveOption }}
            />
          </Form.Item>
        </Col>
      </Row>
      {sectionProps.hideTimesheetApproverSection === true ? null : (
        <Row>
          <Col span="11">
            <Form.Item
              label={
                <FormattedMessage id="settings.team_members.invite.label.timesheet.approver" />
              }
              name="timeSheetApprover"
            >
              <Select
                type="user"
                options={approverOptions}
                menuPlacement="auto"
              />
            </Form.Item>
          </Col>
        </Row>
      )}

      {sectionProps.hideCapacitySection === true ? null : (
        <Row>
          <Col span="24">
            <StyledFormItem
              name="defaultCapacity"
              label={
                <FormattedMessage id="settings.team_members.capacity.title" />
              }
            >
              <>
                <Radio.Group
                  value={defaultCapacity}
                  onChange={(e) => {
                    setDefaultCapacity(e.target.value);
                    if (member == null && capacity == null) {
                      setCapacity(company.capacity);
                    }
                    form.setFieldsValue({
                      defaultCapacity: e.target.value,
                      capacity: e.target.value ? company.capacity : capacity,
                    });
                  }}
                >
                  <Space direction="vertical">
                    <Radio value>
                      <FormattedMessage
                        id="settings.team_members.capacity.options.default"
                        values={{
                          hours: company.capacity,
                          strong: (chunks) => <strong>{chunks}</strong>,
                        }}
                      />
                    </Radio>
                    <Radio value={false}>
                      <FormattedMessage id="settings.team_members.capacity.options.manual" />
                    </Radio>
                  </Space>
                </Radio.Group>
                <InnerRow>
                  <Col offset="1" span="10">
                    <StyledFormItem
                      name="capacity"
                      initialValue={capacity}
                      hidden={defaultCapacity}
                      rules={[
                        () => ({
                          validator(_, value) {
                            const x = Number(
                              removeInsignificantTrailingZero(value),
                            );

                            if (
                              x <= 0 ||
                              x > duration(24, 'hours').asHours() * 7 // * Keeping 27*7 as max week capacity
                            ) {
                              return Promise.reject(
                                new Error(capacityInputError),
                              );
                            }

                            return Promise.resolve();
                          },
                        }),
                      ]}
                    >
                      <InputNumber
                        autoFocus
                        placeholder={capacityInputPlaceholder}
                        formatter={(value) => `${value}h`}
                        parser={(value) => value?.replace('h', '') ?? ''}
                        onChange={() => {
                          setCapacity(form.getFieldValue('capacity'));
                          form.setFieldsValue({
                            capacity: Number(
                              removeInsignificantTrailingZero(
                                form.getFieldValue('capacity'),
                              ),
                            ),
                          });
                          form.validateFields(['capacity']);
                        }}
                      />
                    </StyledFormItem>
                  </Col>
                </InnerRow>
              </>
            </StyledFormItem>
          </Col>
        </Row>
      )}
      {sectionProps.hideFieldsSection === true ? null : (
        <TopMarginFormItem name="fields">
          <TeamMemberSectionField
            fields={member?.user.fields}
            readOnly={false}
            onFieldValueChange={onCustomFieldValueChange}
          />
        </TopMarginFormItem>
      )}
    </Form>
  );
};
